import { LuArrowDownAZ } from '@react-icons/all-files/lu/LuArrowDownAZ'
import { LuArrowUpAZ } from '@react-icons/all-files/lu/LuArrowUpAZ'
import { LuCalendar } from '@react-icons/all-files/lu/LuCalendar'
import { LuCheck } from '@react-icons/all-files/lu/LuCheck'
import { LuClock } from '@react-icons/all-files/lu/LuClock'
import { LuHistory } from '@react-icons/all-files/lu/LuHistory'
import { LuSlidersHorizontal } from '@react-icons/all-files/lu/LuSlidersHorizontal'
import { LuTag } from '@react-icons/all-files/lu/LuTag'
import React from 'react'

import {
  AccessibleIcon,
  DropdownMenu,
  Flex,
  IconButton,
  Theme,
} from 'components/ds'

import type { Option, SortIcon } from '../select-options'
import { tagFilterOption } from '../select-options'

const ICON_SIZE = 14
const sortIconMap: Record<SortIcon, React.ReactNode> = {
  'alpha-asc': <LuArrowDownAZ size={ICON_SIZE} />,
  'alpha-desc': <LuArrowUpAZ size={ICON_SIZE} />,
  'date-newest': <LuClock size={ICON_SIZE} />,
  'date-oldest': <LuHistory size={ICON_SIZE} />,
  calendar: <LuCalendar size={ICON_SIZE} />,
  tag: <LuTag size={ICON_SIZE} />,
}

const groupOptions = (options: Option[]): Map<string, Option[]> => {
  const groups = new Map<string, Option[]>()
  for (const option of options) {
    const label = option.group ?? 'Sort'
    const group = groups.get(label) ?? []
    group.push(option)
    groups.set(label, group)
  }
  return groups
}

type Props = {
  options: Option[]
  selectedValue: string | null
  onOptionClick: (value: string) => void
}

export function BrowseSortMenu({
  options,
  selectedValue,
  onOptionClick,
}: Props) {
  const sortOptions = options.filter(o => o.value !== tagFilterOption.value)
  const tagOption = options.find(o => o.value === tagFilterOption.value)
  const groups = groupOptions(sortOptions)

  const renderItem = (option: Option) => (
    <DropdownMenu.Item
      key={option.value}
      onSelect={() => onOptionClick(option.value)}
    >
      <Flex align="center" gap="2" flexGrow="1">
        {option.sortIcon && <span>{sortIconMap[option.sortIcon]}</span>}
        <span>{option.label}</span>
      </Flex>
      {option.value !== tagFilterOption.value &&
        selectedValue === option.value && <LuCheck size={ICON_SIZE} />}
    </DropdownMenu.Item>
  )

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton variant="ghost" size="1" color="gray" highContrast>
          <AccessibleIcon label="Sort and filter">
            <LuSlidersHorizontal size={16} />
          </AccessibleIcon>
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content variant="solid" align="end" size="2">
        <Theme appearance="light">
          {[...groups.entries()].map(([label, groupOptions], i) => (
            <React.Fragment key={label}>
              {i > 0 && <DropdownMenu.Separator />}
              <DropdownMenu.Label>{label}</DropdownMenu.Label>
              {groupOptions.map(option => renderItem(option))}
            </React.Fragment>
          ))}
          {tagOption && (
            <>
              <DropdownMenu.Separator />
              {renderItem(tagOption)}
            </>
          )}
        </Theme>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
